import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../../db/local.js';
import { dayStory } from '../../db/diary.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { useModules } from '../../hooks/useModules.js';

/**
 * „Was war“ (18.10.2026): Der Tag schreibt sich selbst aus dem, was schon in
 * der App steht. Schreiben bleibt ein Zusatz, keine Pflicht — und was fehlt,
 * steht nicht da, statt sich als leere Zeile auszugeben.
 */
export function DayStory({ date }: { date: string }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const modules = useModules();
  const story = useLiveQuery(
    () => (timeZone ? dayStory(date, timeZone, db) : undefined),
    [date, timeZone],
  );
  if (!story) return null;

  const lines: string[] = story.bucketDone.map((title) => t('bucket.story', { title }));
  if (modules.includes('training')) {
    for (const training of story.trainings) {
      lines.push(
        [
          t('diary.training', { title: training.title }),
          training.minutes !== null ? t('diary.minutes', { count: training.minutes }) : null,
          training.sets > 0 ? t('diary.sets', { count: training.sets }) : null,
        ]
          .filter(Boolean)
          .join(' · '),
      );
    }
  }
  if (modules.includes('nutrition')) {
    if (story.food) {
      const reached =
        story.food.proteinTarget !== null && story.food.protein >= story.food.proteinTarget;
      lines.push(
        [
          t('diary.food', { kcal: story.food.kcal, protein: story.food.protein }),
          reached ? t('diary.proteinReached') : null,
        ]
          .filter(Boolean)
          .join(' · '),
      );
    }
    if (story.waterMl > 0) {
      lines.push(
        t('diary.water', {
          liters: (story.waterMl / 1000).toLocaleString(i18n.language, {
            maximumFractionDigits: 2,
          }),
        }),
      );
    }
  }
  if (modules.includes('planning')) {
    for (const event of story.events) {
      lines.push(event.time ? `${event.time} · ${event.title}` : event.title);
    }
    if (story.tasksDone.length > 0) {
      lines.push(
        t('diary.tasksDone', {
          count: story.tasksDone.length,
          list: story.tasksDone.slice(0, 3).join(', '),
        }),
      );
    }
  }
  if (lines.length === 0) return null;

  return (
    <section aria-labelledby={`story-${date}`} className="kw-tile flex flex-col gap-2">
      <h3 id={`story-${date}`} className="kw-kicker">
        {t('diary.storyHeading')}
      </h3>
      <ul className="flex flex-col gap-1 text-sm">
        {lines.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    </section>
  );
}
